import React, { useCallback, useEffect, useState } from "react";
import CTAButton from "../../components/button/CTAButton";
import ChevronRight from "../../assets/icons/ChevronRight";
import Text from "../../components/Text/Text";
import { useQuery } from "@tanstack/react-query";
import { httpCommunityClient } from "../../shared/http-client";
import {
    BULK_FOLLOW_COMMS,
    GET_COMMS_ONBOARDING_SUGGESTIONS,
} from "../../shared/constants/URLS";
import { Community, suggestionParser } from "./utils/commsParser";
import { MIN_COMM_SELECTION } from "./contants";
import CommunityOnboardingLoading, {
    LOGOS_TO_SHOW,
} from "./components/community-onboarding-loading";
import CommunityLogo from "./components/community-logo";
import { finishWebViewWithProps } from "../../shared/appInterface";
import { CircularProgress } from "@mui/material";
import TickGradient from "../../assets/icons/TickGradient";
import CommunityBox from "./components/CommunityBox";
import CrossIcon from "../../assets/icons/cross-icon";
import { trackMixpanelEvent } from "../../shared/utils/helpers";
import {
    COMM_ADD_CLICK,
    INTERST_SHOWN,
    LOADING_SCREEN,
    PRIMARY_CLICK,
} from "./utils/mixpanel";
import Loader from "../../components/loader/loader";

const CommunityOnboardingStepTwo: React.FC<{}> = () => {
    const [selectedCommunities, setSelectedCommunities] = useState<Community[]>([]);
    const [showCommuntiesFinalisedScreen, setShowCommuntiesFinalisedScreen] =
        useState<boolean>(false);

    const { data: topicSuggestions, isLoading: isCommsDetailsLoading } = useQuery(
        ["communitySuggestions"],
        () => {
            return httpCommunityClient
                .get(GET_COMMS_ONBOARDING_SUGGESTIONS)
                .then((response) => {
                    if (response?.data?.data) {
                        return suggestionParser(response.data.data);
                    }
                });
        }
    );

    const toggleCommunityHandler = (community: Community) => {
        setSelectedCommunities((prevCommunities) => {
            const isCommunitySelected = prevCommunities.some(
                (c) => c.id === community.id
            );

            if (isCommunitySelected) {
                // If community is already selected, remove it
                return prevCommunities.filter((c) => c.id !== community.id);
            } else {
                // If community is not selected, add it
                trackMixpanelEvent(COMM_ADD_CLICK, {
                    "Topic ID": community.id,
                });
                return [...prevCommunities, community];
            }
        });
    };

    const primaryButtonHandler = () => {
        const cIds = selectedCommunities.map((comm) => comm.id);
        trackMixpanelEvent(PRIMARY_CLICK, {
            "Topic ID": cIds,
            is_button_live: selectedCommunities?.length >= MIN_COMM_SELECTION,
        });
        if (selectedCommunities?.length >= MIN_COMM_SELECTION) {
            trackMixpanelEvent(LOADING_SCREEN);
            setShowCommuntiesFinalisedScreen(true);
        }
    };

    const renderCommsTopics = useCallback(() => {
        if (topicSuggestions) {
            return (
                <div className="scrollbarHide grid h-fit w-[full] grid-cols-3 flex-col gap-[16px] px-[16px] pb-[20%] pt-[4px]">
                    {topicSuggestions.map((comm) => {
                        const isCommunitySelected = selectedCommunities.some(
                            (c) => c.id === comm.id
                        );

                        return (
                            <CommunityBox
                                community={comm}
                                key={`commsCarousel_${comm.id}`}
                                isSelected={isCommunitySelected}
                                toggleCommunityHandler={toggleCommunityHandler}
                            />
                        );
                    })}
                </div>
            );
        }

        return <></>;
    }, [topicSuggestions, selectedCommunities]);

    const getFooterHeader = useCallback(() => {
        if (selectedCommunities.length < MIN_COMM_SELECTION) {
            return (
                <div className="align-center flex w-full justify-center">
                    <Text type="B3" color="#CC0000">
                        {`Select at least ${
                            MIN_COMM_SELECTION - selectedCommunities.length
                        } ${ selectedCommunities.length > 0 ? "more" : ""} topics to get started`}
                    </Text>
                </div>
            );
        } else if (selectedCommunities.length >= MIN_COMM_SELECTION) {
            return (
                <div className="scrollbarHide w-[100vw] items-center justify-center overflow-x-scroll">
                    <div className="flex w-fit gap-[4px] first:ml-[15px] last:mr-[12px] ">
                        {selectedCommunities.map((comm) => (
                            <div className="flex items-center justify-center gap-[4px] rounded-[100px] border border-solid border-[#1F8268] bg-[#EAF8F4] px-[8px] py-[4px]">
                                <Text type="B3" color="#1F8268" additonalStyle="w-max">
                                    {comm.name}
                                </Text>
                                <div
                                    className="flex items-center justify-center"
                                    onClick={() => {
                                        toggleCommunityHandler(comm);
                                    }}
                                >
                                    <CrossIcon size={10} fill="#1F8268" />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            );
        }
    }, [selectedCommunities]);

    useEffect(() => {
        if (showCommuntiesFinalisedScreen && selectedCommunities.length > 0) {
            const selectedCommIds = selectedCommunities.map((comm) => comm.id);
            const payload = {
                topic_ids: selectedCommIds,
            };
            httpCommunityClient.post(BULK_FOLLOW_COMMS, payload).then(() => {
                setTimeout(() => {
                    finishWebViewWithProps(
                        JSON.stringify({ isCommunityOnBoardingSuccessful: true })
                    );
                }, 3000);
            });
        }
    }, [showCommuntiesFinalisedScreen, selectedCommunities]);

    useEffect(() => {
        if (
            !isCommsDetailsLoading &&
            topicSuggestions &&
            !showCommuntiesFinalisedScreen
        ) {
            const topicIds = topicSuggestions.map((comm) => {
                return comm.id;
            });

            trackMixpanelEvent(INTERST_SHOWN, {
                "Topic IDs": topicIds,
            });
        }
    }, [isCommsDetailsLoading, topicSuggestions, showCommuntiesFinalisedScreen]);

    return (
        <>
            {showCommuntiesFinalisedScreen && (
                <CommunityOnboardingLoading selectedCommunties={selectedCommunities} />
            )}
            {!showCommuntiesFinalisedScreen && (
                <div className="h-[100vh] w-full font-[`Inter`] text-[#190A28]">
                    <div className="flex w-full items-center gap-[4px] px-[16px] py-[8px]">
                        <div className="flex gap-[0.5px]">
                            <Text type="H2" color="#190A28">
                                apna
                            </Text>
                            <Text type="H2" color="#1F8268">
                                Community
                            </Text>
                        </div>
                        <div>
                            <TickGradient />
                        </div>
                    </div>

                    <hr className="solid" />
                    <div className="flex w-full flex-col gap-[8px] break-words px-[16px] py-[8px]">
                        <Text type="T1">What topics are you interested in?</Text>
                        <Text type="B2" color="#8C8594">
                            Select topics of your interests so that we can personalise
                            your feed
                        </Text>
                    </div>
                    <div className="scrollbarHide h-[calc(100vh-180px)] w-full overflow-y-scroll">
                        {isCommsDetailsLoading ? (
                            <div className="flex h-[80%] w-full items-center justify-center">
                                <Loader />
                            </div>
                        ) : (
                            <> {renderCommsTopics()}</>
                        )}
                    </div>

                    <div className=" fixed bottom-0 z-20 flex h-fit w-full flex-col gap-[12px] bg-[#FFFF] pt-[12px] pb-[16px] shadow-[0px_-2px_8px_0px_rgba(0,0,0,0.04)]">
                        {getFooterHeader()}
                        <div className="w-full px-[16px]">
                            <CTAButton
                                onClick={primaryButtonHandler}
                                disabled={
                                    !(selectedCommunities?.length >= MIN_COMM_SELECTION)
                                }
                                sx={{
                                    ":disabled": {
                                        opacity: 0.5,
                                    },
                                }}
                                endIcon={<ChevronRight />}
                            >
                                Next
                            </CTAButton>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default CommunityOnboardingStepTwo;
